import { useState, useEffect, useCallback } from 'react';
import {
  Plus, Trash2, Edit3, Search, Video, AlertTriangle, X, Shield,
} from 'lucide-react';

const SKU_OPTIONS = [
  { value: 'RA_TRIAL',      label: 'Trial (14 days)',       maxCameras: 10 },
  { value: 'RA_STARTER_25', label: 'Starter (25 cameras)',  maxCameras: 25 },
  { value: 'RA_PRO_100',    label: 'Pro (100 cameras)',     maxCameras: 100 },
  { value: 'RA_ENTERPRISE', label: 'Enterprise (Unlimited)', maxCameras: 9999 },
];

const STATUS_COLORS = {
  TRIAL:     { bg: 'rgba(168, 85, 247, 0.15)', color: '#a855f7' },
  ACTIVE:    { bg: 'rgba(34, 197, 94, 0.15)',  color: '#22c55e' },
  EXPIRED:   { bg: 'rgba(239, 68, 68, 0.15)',  color: '#ef4444' },
  SUSPENDED: { bg: 'rgba(245, 158, 11, 0.15)', color: '#f59e0b' },
};

const formatDate = (s) => s
  ? new Date(s).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
  : '—';

const RA_BASE = '/api/platform/ra';

const RaLicensesTab = ({ tenants }) => {
  const [licenses, setLicenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingLicense, setEditingLicense] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const [formData, setFormData] = useState({
    tenantId: '',
    sku: 'RA_TRIAL',
    expiresAt: '',
    notes: '',
  });

  const fetchLicenses = useCallback(async () => {
    try {
      const res = await fetch(`${RA_BASE}/all`, { credentials: 'include' });
      if (res.ok) setLicenses(await res.json());
    } catch (e) {
      console.error('Failed to fetch RA licenses:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchLicenses(); }, [fetchLicenses]);

  const licensedTenantIds = licenses.map(l => l.tenantId);
  const unlicensedTenants = (tenants || []).filter(t => !licensedTenantIds.includes(t.id));

  const handleSave = async (isEdit) => {
    const tenantId = isEdit ? editingLicense.tenantId : formData.tenantId;
    if (!tenantId) return;
    try {
      const res = await fetch(RA_BASE, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-tenant-id': tenantId },
        credentials: 'include',
        body: JSON.stringify({
          tenantId,
          sku: formData.sku,
          expiresAt: formData.expiresAt || null,
          notes: formData.notes || null,
        }),
      });
      if (res.ok) {
        setShowCreateModal(false);
        setEditingLicense(null);
        resetForm();
        fetchLicenses();
      } else {
        const err = await res.json().catch(() => ({}));
        alert(err.error || 'Failed to save license');
      }
    } catch (e) {
      console.error(e);
    }
  };

  const handleRevoke = async () => {
    if (!deleteTarget) return;
    try {
      const res = await fetch(`${RA_BASE}/${deleteTarget.tenantId}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (res.ok) {
        setDeleteTarget(null);
        fetchLicenses();
      } else {
        const err = await res.json().catch(() => ({}));
        alert(err.error || 'Failed to revoke license');
      }
    } catch (e) {
      console.error(e);
    }
  };

  const resetForm = () => setFormData({ tenantId: '', sku: 'RA_TRIAL', expiresAt: '', notes: '' });

  const openEdit = (lic) => {
    setFormData({
      tenantId: lic.tenantId,
      sku: lic.sku,
      expiresAt: lic.expiresAt ? new Date(lic.expiresAt).toISOString().slice(0, 10) : '',
      notes: lic.notes || '',
    });
    setEditingLicense(lic);
  };

  const filtered = licenses.filter(l => {
    if (!searchTerm) return true;
    const s = searchTerm.toLowerCase();
    return l.tenant?.name?.toLowerCase().includes(s)
      || l.sku?.toLowerCase().includes(s)
      || l.status?.toLowerCase().includes(s);
  });

  const isExpired = (lic) =>
    lic.status === 'EXPIRED'
    || (lic.status === 'TRIAL' && lic.trialEndsAt && new Date(lic.trialEndsAt) < new Date());

  const trialDaysRemaining = (lic) => lic.trialEndsAt
    ? Math.max(0, Math.ceil((new Date(lic.trialEndsAt) - new Date()) / 86400000))
    : null;

  if (loading) {
    return <div className="platform-loading"><div className="platform-spinner" /></div>;
  }

  return (
    <div className="md-licenses-tab">
      <div className="md-lic-stats">
        <div className="md-lic-stat">
          <span className="md-lic-stat-value">{licenses.length}</span>
          <span className="md-lic-stat-label">Total Licenses</span>
        </div>
        <div className="md-lic-stat">
          <span className="md-lic-stat-value" style={{ color: '#22c55e' }}>
            {licenses.filter(l => l.status === 'ACTIVE').length}
          </span>
          <span className="md-lic-stat-label">Active</span>
        </div>
        <div className="md-lic-stat">
          <span className="md-lic-stat-value" style={{ color: '#a855f7' }}>
            {licenses.filter(l => l.status === 'TRIAL').length}
          </span>
          <span className="md-lic-stat-label">Trial</span>
        </div>
        <div className="md-lic-stat">
          <span className="md-lic-stat-value" style={{ color: '#ef4444' }}>
            {licenses.filter(isExpired).length}
          </span>
          <span className="md-lic-stat-label">Expired</span>
        </div>
      </div>

      <div className="md-lic-toolbar">
        <div className="md-lic-search">
          <Search size={16} />
          <input
            type="text"
            placeholder="Search tenants..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
          />
        </div>
        <button className="btn btn-primary" onClick={() => { resetForm(); setShowCreateModal(true); }}>
          <Plus size={16} /> Assign License
        </button>
      </div>

      {filtered.length === 0 ? (
        <div className="platform-empty">
          <div className="platform-empty-icon"><Shield size={32} /></div>
          <h3>No Recording Assurance Licenses</h3>
          <p>Assign RA licenses to tenants to enable recording verification.</p>
          <button className="btn btn-primary" onClick={() => { resetForm(); setShowCreateModal(true); }}>
            <Plus size={16} /> Assign License
          </button>
        </div>
      ) : (
        <div className="md-lic-table-wrap">
          <table className="md-lic-table">
            <thead>
              <tr>
                <th>Tenant</th>
                <th>Plan</th>
                <th>Status</th>
                <th>Cameras</th>
                <th>Expires</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(lic => {
                const expired = isExpired(lic);
                const statusStyle = STATUS_COLORS[expired ? 'EXPIRED' : lic.status] || STATUS_COLORS.SUSPENDED;
                const percent = lic.maxCameras > 0
                  ? Math.round(((lic.currentCameras || 0) / lic.maxCameras) * 100)
                  : 0;
                const over = (lic.currentCameras || 0) > lic.maxCameras;
                const daysLeft = trialDaysRemaining(lic);
                return (
                  <tr key={lic.id}>
                    <td>
                      <div className="md-lic-tenant">
                        <span className="md-lic-tenant-name">{lic.tenant?.name || 'Unknown'}</span>
                        <span className="md-lic-tenant-id">{lic.tenantId?.slice(0, 8)}...</span>
                      </div>
                    </td>
                    <td><span className="md-lic-sku">{lic.sku}</span></td>
                    <td>
                      <span
                        className="md-lic-status"
                        style={{ background: statusStyle.bg, color: statusStyle.color }}
                      >
                        {expired ? 'EXPIRED' : lic.status}
                        {lic.status === 'TRIAL' && daysLeft !== null && !expired && (
                          <span className="md-lic-trial-days">{daysLeft}d left</span>
                        )}
                      </span>
                    </td>
                    <td>
                      <div className="md-lic-usage">
                        <span className={over ? 'md-lic-over' : ''}>
                          {lic.currentCameras || 0} / {lic.maxCameras === 9999 ? '∞' : lic.maxCameras}
                        </span>
                        <div className="md-lic-bar">
                          <div
                            className={`md-lic-bar-fill ${over ? 'over' : percent > 80 ? 'warn' : ''}`}
                            style={{ width: `${Math.min(100, percent)}%` }}
                          />
                        </div>
                      </div>
                    </td>
                    <td>
                      {lic.status === 'TRIAL' && lic.trialEndsAt
                        ? formatDate(lic.trialEndsAt)
                        : lic.expiresAt ? formatDate(lic.expiresAt) : 'Never'}
                    </td>
                    <td>
                      <div className="md-lic-actions">
                        <button className="btn btn-secondary btn-sm" onClick={() => openEdit(lic)} title="Edit">
                          <Edit3 size={14} />
                        </button>
                        <button className="btn btn-danger btn-sm" onClick={() => setDeleteTarget(lic)} title="Revoke">
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {(showCreateModal || editingLicense) && (
        <div
          className="platform-modal-overlay"
          onClick={() => { setShowCreateModal(false); setEditingLicense(null); }}
        >
          <div className="platform-modal" onClick={e => e.stopPropagation()}>
            <div className="platform-modal-header">
              <h3>
                {editingLicense
                  ? <><Edit3 size={18} /> Edit RA License — {editingLicense.tenant?.name}</>
                  : <><Plus size={18} /> Assign Recording Assurance License</>}
              </h3>
              <button onClick={() => { setShowCreateModal(false); setEditingLicense(null); }}>
                <X size={18} />
              </button>
            </div>
            <div className="platform-modal-body">
              {!editingLicense && (
                <div className="form-group">
                  <label>Tenant</label>
                  <select
                    className="form-select"
                    value={formData.tenantId}
                    onChange={e => setFormData(p => ({ ...p, tenantId: e.target.value }))}
                  >
                    <option value="">Select tenant...</option>
                    {unlicensedTenants.map(t => (
                      <option key={t.id} value={t.id}>{t.name}</option>
                    ))}
                  </select>
                </div>
              )}
              <div className="form-group">
                <label>Plan</label>
                <select
                  className="form-select"
                  value={formData.sku}
                  onChange={e => setFormData(p => ({ ...p, sku: e.target.value }))}
                >
                  {SKU_OPTIONS.map(s => (
                    <option key={s.value} value={s.value}>{s.label}</option>
                  ))}
                </select>
              </div>
              {formData.sku !== 'RA_TRIAL' && (
                <div className="form-group">
                  <label>Expiration Date</label>
                  <input
                    type="date"
                    className="form-input"
                    value={formData.expiresAt}
                    onChange={e => setFormData(p => ({ ...p, expiresAt: e.target.value }))}
                  />
                </div>
              )}
              <div className="form-group">
                <label>Notes</label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.notes}
                  onChange={e => setFormData(p => ({ ...p, notes: e.target.value }))}
                  placeholder="Optional"
                />
              </div>
            </div>
            <div className="platform-modal-footer">
              <button
                className="btn btn-secondary"
                onClick={() => { setShowCreateModal(false); setEditingLicense(null); }}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={() => handleSave(!!editingLicense)}
                disabled={!editingLicense && !formData.tenantId}
              >
                {editingLicense ? 'Save Changes' : 'Assign License'}
              </button>
            </div>
          </div>
        </div>
      )}

      {deleteTarget && (
        <div className="platform-modal-overlay" onClick={() => setDeleteTarget(null)}>
          <div className="platform-modal delete" onClick={e => e.stopPropagation()}>
            <div className="platform-modal-header">
              <h3><AlertTriangle size={18} className="icon-danger" /> Revoke License</h3>
              <button onClick={() => setDeleteTarget(null)}><X size={18} /></button>
            </div>
            <div className="platform-modal-body">
              <p className="platform-delete-message">
                This will revoke the Recording Assurance license for this tenant. They will lose access to RA features.
              </p>
              <div className="platform-delete-item">{deleteTarget.tenant?.name}</div>
            </div>
            <div className="platform-modal-footer">
              <button className="btn btn-secondary" onClick={() => setDeleteTarget(null)}>Cancel</button>
              <button className="btn btn-danger" onClick={handleRevoke}>Revoke License</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RaLicensesTab;
